import viewService from "@/services/viewService";
import EntityService from "@/services/entityService";

const fisDocEmiService = new EntityService("FisDocEmi");
const clienteService = new EntityService("Cliente");
const fornecedorService = new EntityService("Fornecedor");
const fisPreDocEmiService = new EntityService("FisPreDocEmi");
const projectService = new EntityService("Project");

const toNumber = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const isPossivelId = (valor) => {
  if (valor === null || valor === undefined) return false;
  const texto = String(valor).trim();
  return /^[0-9a-f]{24}$/i.test(texto) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(texto);
};

const primeiroValor = (...valores) => {
  for (const valor of valores) {
    if (valor !== null && valor !== undefined && String(valor).trim() !== "") {
      return valor;
    }
  }

  return undefined;
};

const obterNotaFiscalPrincipal = (linha = {}) => {
  if (Array.isArray(linha?.notaFiscal) && linha.notaFiscal.length) {
    return linha.notaFiscal[0] || {};
  }

  return linha?.notaFiscal || {};
};

const extrairListaGetAll = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const carregarMapaPorIds = async (service, ids = []) => {
  const idsValidos = [...new Set(ids.filter((id) => isPossivelId(id)).map((id) => String(id)))];
  if (!idsValidos.length) return {};

  try {
    const response = await service.getAll(1, Math.max(100, idsValidos.length), "", "", idsValidos, null, null, null);
    const lista = extrairListaGetAll(response);

    return (Array.isArray(lista) ? lista : []).reduce((acc, item) => {
      const id = String(item?.id || item?._id || "");
      if (id) acc[id] = item;
      return acc;
    }, {});
  } catch (error) {
    return {};
  }
};

const enriquecerComRelacionamentos = async (lista = []) => {
  const listaBase = Array.isArray(lista) ? lista : [];

  const preNotaIds = listaBase.map((item) => item?.preNota).filter((valor) => isPossivelId(valor));
  const mapaPreNotas = await carregarMapaPorIds(fisPreDocEmiService, preNotaIds);

  const clienteIds = listaBase
    .map((item) => item?.cliente || mapaPreNotas[String(item?.preNota || "")]?.cliente)
    .filter((valor) => isPossivelId(valor));

  const fornecedorIds = listaBase
    .map((item) => item?.fornecedor || mapaPreNotas[String(item?.preNota || "")]?.fornecedor)
    .filter((valor) => isPossivelId(valor));

  const jobIds = listaBase
    .map((item) => item?.job || item?.project || mapaPreNotas[String(item?.preNota || "")]?.job || mapaPreNotas[String(item?.preNota || "")]?.project)
    .filter((valor) => isPossivelId(valor));

  const [mapaClientes, mapaFornecedores, mapaJobs] = await Promise.all([
    carregarMapaPorIds(clienteService, clienteIds),
    carregarMapaPorIds(fornecedorService, fornecedorIds),
    carregarMapaPorIds(projectService, jobIds)
  ]);

  return listaBase.map((item) => {
    const preNota = mapaPreNotas[String(item?.preNota || "")] || {};
    const cliente = mapaClientes[String(item?.cliente || preNota?.cliente || "")] || {};
    const fornecedor = mapaFornecedores[String(item?.fornecedor || preNota?.fornecedor || "")] || {};
    const jobId = String(item?.job || item?.project || preNota?.job || preNota?.project || "");
    const job = mapaJobs[jobId] || {};

    const entidadeDestino = item?.fornecedor || preNota?.fornecedor ? fornecedor : cliente;

    return {
      ...item,
      cnpjDestinoResolvido: primeiroValor(
        item?.cnpj,
        item?.dest_cnpj,
        item?.destinatarioCpfCnpj,
        item?.destinatarioCnpj,
        preNota?.cnpj,
        entidadeDestino?.cnpj,
        entidadeDestino?.cpfCnpj,
        entidadeDestino?.documento
      ),
      razaoSocialDestinoResolvida: primeiroValor(
        item?.destinatarioNome,
        item?.destinatarioFornecedorNome,
        item?.dest_razao,
        item?.razaoSocial,
        preNota?.destinatarioNome,
        entidadeDestino?.nomeFantasia,
        entidadeDestino?.razaoSocial,
        entidadeDestino?.nome
      ),
      cfopResolvido: primeiroValor(item?.cfop, preNota?.cfop)
      ,
      tipoNotaResolvido: primeiroValor(
        item?.tipoNota,
        item?.tipoDocumento,
        item?.tipoDoc,
        item?.tipoNF,
        item?.tipoNf,
        item?.tipo,
        item?.notaFiscal?.tipoNota,
        item?.notaFiscal?.tipoDocumento,
        item?.notaFiscal?.tipo,
        preNota?.tipoNota,
        preNota?.tipoDocumento,
        preNota?.tipo
      ),
      jobCodeResolvido: primeiroValor(
        item?.jobCode,
        item?.job_codigo,
        item?.codigoJob,
        item?.job?.code,
        item?.project?.code,
        preNota?.jobCode,
        preNota?.job_codigo,
        preNota?.codigoJob,
        preNota?.job?.code,
        preNota?.project?.code,
        job?.code,
        job?.codigo,
        isPossivelId(jobId) ? undefined : jobId
      )
    };
  });
};

const normalizarLinha = (linha = {}) => {
  const notaFiscal = obterNotaFiscalPrincipal(linha);
  const vlrICMS = toNumber(linha.icmsValor || linha.vlrICMS || linha.icms || notaFiscal.icmsValor || notaFiscal.vlrICMS || notaFiscal.icms);
  const vlrIPI = toNumber(linha.ipiValor || linha.vlrIPI || linha.ipi || notaFiscal.ipiValor || notaFiscal.vlrIPI || notaFiscal.ipi);
  const vlrPIS = toNumber(linha.pisValor || linha.vlrPIS || linha.pis || notaFiscal.pisValor || notaFiscal.vlrPIS || notaFiscal.pis);
  const vlrCOFINS = toNumber(linha.cofinsValor || linha.vlrCOFINS || linha.cofins || notaFiscal.cofinsValor || notaFiscal.vlrCOFINS || notaFiscal.cofins);
  const vlrISS = toNumber(linha.issValor || linha.vlrISS || linha.iss || notaFiscal.issValor || notaFiscal.vlrISS || notaFiscal.iss);
  const vlrICMSST = toNumber(linha.vlrICMSST || linha.icmsST || linha.vICMSST || notaFiscal.vlrICMSST || notaFiscal.icmsST || notaFiscal.vICMSST);
  const vlrOutrosImpostos = toNumber(
    linha.vlrOutrosImpostos ||
    linha.outrosImpostos ||
    linha.vlrTributos ||
    linha.tributos ||
    notaFiscal.vlrOutrosImpostos ||
    notaFiscal.outrosImpostos ||
    notaFiscal.vlrTributos ||
    notaFiscal.tributos
  );
  const impostosCalculados = vlrICMS + vlrIPI + vlrPIS + vlrCOFINS + vlrISS + vlrICMSST + vlrOutrosImpostos;
  const impostosTotais = toNumber(linha.impostos || notaFiscal.impostos || impostosCalculados);

  const valorTotalComImpostos = toNumber(
    linha.notaFiscalValor ||
    notaFiscal.notaFiscalValor ||
    0
  );

  return {
    status: linha.statusEmissao || linha.status || "-",
    isDeleted: primeiroValor(linha.isDeleted, notaFiscal.isDeleted, false),
    tipoNota: primeiroValor(
      linha.tipoNotaResolvido,
      linha.tipoNota,
      linha.tipoDocumento,
      linha.tipoDoc,
      linha.tipoNF,
      linha.tipoNf,
      linha.tipo,
      linha.infoNf?.tipoNota,
      linha.infoNf?.tipoDocumento,
      linha.infoNf?.tipo,
      linha.naturezaOperacao,
      notaFiscal.tipoNota,
      notaFiscal.tipoDocumento,
      notaFiscal.tipo,
      notaFiscal.naturezaOperacao
    ) || "-",
    data: primeiroValor(linha.dataEmissao, linha.data, notaFiscal.dataEmissao, notaFiscal.createAt, linha.createAt) || null,
    serie: primeiroValor(linha.serie, notaFiscal.serie) || "-",
    numero: primeiroValor(linha.numero, notaFiscal.numero) || "-",
    cnpj: primeiroValor(
      linha.cnpjDestinoResolvido,
      linha.dest_cnpj,
      linha.destinatarioCpfCnpj,
      linha.destinatarioCnpj,
      linha.cnpj,
      notaFiscal.cnpj,
      notaFiscal.destinatarioCpfCnpj,
      notaFiscal.destinatarioCnpj,
      linha.destinatario?.cpfCnpj,
      linha.destinatarioInfo?.[0]?.cnpjCpf,
      linha.destinatarioInfo?.[0]?.cpfCnpj,
      linha.destinatarioInfo?.[0]?.cnpj,
      linha.emit_cnpj
    ) || "-",
    razaoSocial: primeiroValor(
      linha.razaoSocialDestinoResolvida,
      linha.destinatarioNome,
      linha.destinatarioFornecedorNome,
      linha.dest_razao,
      linha.razaoSocial,
      linha.clienteNome,
      notaFiscal.destinatarioNome,
      notaFiscal.razaoSocial,
      linha.destinatario?.razaoSocial,
      linha.destinatarioInfo?.[0]?.razaoSocial,
      linha.destinatarioInfo?.[0]?.nomeFantasia
    ) || "-",
    cfop: primeiroValor(linha.cfopResolvido, linha.cfop, notaFiscal.cfop, linha.infoNf?.cfop) || "-",
    job: primeiroValor(linha.jobCodeResolvido, linha.jobCode, linha.codigoJob, linha.job_codigo, linha.job, linha.project) || "-",
    valorTotal: valorTotalComImpostos,
    impostos: impostosTotais
  };
};

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const possuiDadosValidos = (response) => {
  if (!response) return false;
  if (response?.success === false) return false;
  if (response?.data?.success === false) return false;
  return Array.isArray(response?.data?.data?.data) || Array.isArray(response?.data);
};

const listarPorEntidade = async () => {
  const response = await fisDocEmiService.getAll(1, 5000, "", "", null, null, null, "dataEmissao");
  return extrairLista(response);
};

const listarPorView = async (params = []) => {
  const viewNames = [
    "relatorio-nota-fiscal-emitida",
    "a619bda4-4cf3-4d84-975b-a2535c76a02b",
    "fisdocemi"
  ];

  for (const viewName of viewNames) {
    const response = await viewService.viewRun(viewName, 1, params);
    if (possuiDadosValidos(response)) {
      return extrairLista(response);
    }
  }

  return [];
};

export default {
  async listarNFEmitida(filtros = {}) {
    const params = [];

    if (filtros.dataInicio) {
      params.push({ dataType: "String", name: "dataInicio", value: filtros.dataInicio });
    }

    if (filtros.dataFim) {
      params.push({ dataType: "String", name: "dataFim", value: filtros.dataFim });
    }

    if (filtros.status) {
      params.push({ dataType: "String", name: "status", value: filtros.status });
    }

    if (filtros.razaoSocial) {
      params.push({ dataType: "String", name: "razaoSocial", value: filtros.razaoSocial });
    }

    let lista = await listarPorView(params);

    if (!lista.length) {
      lista = await listarPorEntidade();
    }

    lista = await enriquecerComRelacionamentos(lista);

    return lista.map((linha) => normalizarLinha(linha));
  }
};
